/**
 * Sanitização de texto livre — mensagem de exceção e componentStack — antes da emissão.
 *
 * A redação aplicada na borda do SDK atua por nome de chave e não alcança texto livre: a
 * mensagem de uma exceção é uma string única, e dado pessoal ou credencial dentro dela
 * passaria intacto. Daí esta camada separada.
 */

/** Teto da mensagem, alinhado ao corte já aplicado a texto livre de erro na borda HTTP. */
export const MESSAGE_MAX_LENGTH = 1000;

/** Teto do componentStack, maior porque a árvore de componentes é naturalmente mais longa. */
export const COMPONENT_STACK_MAX_LENGTH = 4000;

export const EMAIL_MARKER = '[EMAIL]';
export const DOCUMENT_MARKER = '[DOC]';
export const JWT_MARKER = '[JWT]';
export const QUERY_MARKER = '?[QUERY]';
export const BEARER_MARKER = 'Bearer [REDACTED]';

/**
 * Sem exigência de fronteira de palavra: `Bearer` colado ao texto anterior também é
 * redigido. Exigir a fronteira deixaria o token visível justamente quando a redação de
 * documento, aplicada depois, substituísse os dígitos vizinhos e criasse a fronteira.
 */
const BEARER_PATTERN = /Bearer\s+\S+/gi;

/** Mesma razão da regra de Bearer para a ausência de fronteira de palavra. */
const JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

/**
 * Consome tudo até o próximo espaço, então um `?` de pontuação leva a palavra seguinte.
 * Over-redaction aceita: casar `?` apenas com `chave=valor` deixaria passar query string
 * mal formada, que é justamente a forma que aparece em mensagem de erro.
 */
const QUERY_PATTERN = /\?\S*/g;

/**
 * Mais ampla que CPF e CNPJ de propósito: uma corrida de 12 ou 13 dígitos não é nenhum dos
 * dois e continua reidentificável. Rende também um invariante simples de asseverar — a
 * saída nunca carrega 11 ou mais dígitos consecutivos.
 */
const DOCUMENT_PATTERN = /\d{11,}/g;

/**
 * Sanitiza texto livre e trunca no teto informado. Nunca lança e sempre devolve string,
 * inclusive quando a coerção do valor para texto falha.
 *
 * A ordem é significativa. `Bearer` vem antes de JWT porque elimina o token inteiro em vez
 * de deixar o prefixo órfão; o truncamento vem por último porque cortar antes de sanitizar
 * deixaria metade de um JWT ou de um e-mail — fragmento que nenhum dos padrões reconhece.
 */
export function scrubMessage(value: unknown, maxLength: number): string {
  let text: string;

  try {
    text = typeof value === 'string' ? value : String(value);
  } catch {
    return '';
  }

  return text
    .replace(BEARER_PATTERN, BEARER_MARKER)
    .replace(JWT_PATTERN, JWT_MARKER)
    .replace(EMAIL_PATTERN, EMAIL_MARKER)
    .replace(QUERY_PATTERN, QUERY_MARKER)
    .replace(DOCUMENT_PATTERN, DOCUMENT_MARKER)
    .slice(0, maxLength);
}
